import {createContext, type PropsWithChildren, use, useCallback, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {EventHappening, ListChatsResponse} from "@/lib/types";
import {useGlobalSearchParams} from "expo-router";

const EventContext = createContext<{
    event?: EventHappening;
    eventLoading: boolean;
    eventError: Error | null;
    eventChatsLoading: boolean;
    eventChatsError: Error | null;
    eventChatIds?: string[];
    refreshEventChats: () => any;
    refreshEvent: () => any;
}>({
    event: undefined,
    eventLoading: true,
    eventError: null,
    eventChatIds: undefined,
    eventChatsLoading: true,
    eventChatsError: null,
    refreshEventChats: () => {
    },
    refreshEvent: () => {
    },
});

export function useEvent() {
    const value = use(EventContext);
    if (!value) {
        throw new Error('useEvent must be wrapped in a <EventProvider />');
    }
    return value;
}

export function EventProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session, ...sessionParams} = useSession();

    const {event_id} = useGlobalSearchParams();
    const queryKey = useMemo(() => ['event', event_id, session], [event_id, session]);
    const queryKeyAll = useMemo(() => ['event', session], [session]);
    const queryKeyChats = useMemo(() => ['event', event_id, 'chat', session], [event_id, session]);

    const refreshEvent = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey});
        await queryClient.invalidateQueries({queryKey: queryKeyAll});
    }, [queryClient, queryKey, queryKeyAll]);

    const refreshEventChats = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey:queryKeyChats});
    }, [queryClient, queryKeyChats]);

    const {data, isLoading, error} = useQuery<EventHappening>({
        queryKey,
        queryFn: () => fetchBackend(`/event/${event_id}`, "GET", session),
        staleTime: 1000 * 60 * 15,
        retry: dataRetryPolicy,
        enabled: !!event_id && !sessionParams.isLoading,
    });

    const chatsQuery = useQuery<ListChatsResponse>({
        queryKey: queryKeyChats,
        queryFn: () => fetchBackend(`/event/${event_id}/chat`, "GET", session),
        retry: dataRetryPolicy,
        enabled: !!data,
    });

    return (
        <EventContext.Provider
            value={{
                event: data,
                refreshEvent,
                refreshEventChats,
                eventLoading: isLoading,
                eventError: error,
                eventChatsLoading: chatsQuery.isLoading,
                eventChatsError: chatsQuery.error,
                eventChatIds: chatsQuery.data?.chat_ids,
            }}>
            {children}
        </EventContext.Provider>
    );
}